import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Lock, Store, Truck } from "lucide-react";
import { toast } from "sonner";
import { Btn, Chip, Toggle, cellCls } from "@/components/account/Shell";
import { useBackOffice } from "@/components/account/BackOffice";
import api, { formatApiError } from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

function MoneyMap({ example, pct, rate }) {
    const pays = example * (1 + pct / 100);
    const commission = example * rate;
    const markup = pays - example;
    const mine = commission + markup;
    return (
        <div className="border-t border-[#d0d0d0] bg-[#fafafa] p-2.5" data-testid="consign-money-map">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                On a {money(example)} job from this shelf
            </p>
            <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                {[
                    ["13th Ave's own price", money(example)],
                    ["The customer pays you", money(pays)],
                    [`Your ${Math.round(rate * 100)}% commission`, money(commission)],
                    [`Your ${pct}% percent over`, money(markup)],
                    ["You keep", money(mine)],
                    ["13th Ave keeps", money(pays - mine)],
                    ["Who prints, packs and ships it", "13th Ave — box and label included"],
                    ["Who the customer talks to", `You. It's your shop, your name on the door.`],
                ].map(([k, v]) => (
                    <p key={k} className="flex items-baseline justify-between gap-3 border-b border-[#d0d0d0] py-1 text-[13px] last:border-b-0">
                        <span className="text-[#4a4a4a]">{k}</span>
                        <span className="text-right font-semibold tabular-nums text-[#1a1a1a]">{v}</span>
                    </p>
                ))}
            </div>
        </div>
    );
}

/**
 * The consignment menu that sits at the top of a shelf's own page: what's locked, what this
 * shelf is marked up, what a job actually pays, and the two switches a shop still owns.
 */
export default function ConsignmentSectionBar({ room, info, rate = 0.2, maxMarkup = 10,
    example = 15, onChanged }) {
    const { reload } = useBackOffice();
    const [pct, setPct] = useState(String(info?.markup_pct ?? 0));
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    useEffect(() => setPct(String(info?.markup_pct ?? 0)), [info?.markup_pct]);

    if (!info) return null;

    const run = async (fn) => {
        setBusy(true);
        try {
            const { data } = await fn();
            if (data?.note) toast.success(data.note);
            reload();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const saveMarkup = () => run(() => api.put("/store/consignment/room-markup",
        { room, markup_pct: Math.min(Math.max(Number(pct) || 0, 0), maxMarkup) }));
    const useDefault = () => run(() => api.put("/store/consignment/room-markup", { room, markup_pct: null }));
    const setPrivate = (v) => run(() => api.put("/store/consignment/rooms", { room, private: v }));
    const setCounter = (v) => run(async () => {
        const res = await api.put("/stores/me/lines", { lines: { [room]: v } });
        return { data: { ...res.data, note: v ? `Your ${info.label.toLowerCase()} counter is open.` : `Your ${info.label.toLowerCase()} counter is closed — shoppers won't see it.` } };
    });

    if (!info.consigned) return (
        <div className="border border-[#067647] bg-white" data-testid={`consign-bar-${room}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[#d0d0d0] bg-[#ecfdf3] px-2.5 py-1.5">
                <Chip tone="green" testid={`consign-bar-state-${room}`}><Store size={10} /> you print this shelf</Chip>
                <p className="text-[13px] text-[#1a1a1a]">
                    <span className="font-semibold">{info.label} is yours.</span> You print it, you ship it, the money
                    goes to your own Stripe — and everything below is unlocked and editable.
                </p>
                <span className="ml-auto flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase text-[#4a4a4a]">Hand it back to 13th Ave</span>
                    <Toggle on={false} disabled={busy} testid={`consign-hand-back-${room}`}
                        labelOn="Theirs" labelOff="Mine" onChange={() => setPrivate(false)} />
                </span>
            </div>
        </div>
    );

    const live = example * (1 + (Number(pct) || 0) / 100);
    return (
        <div className="border-2 border-[#b42318] bg-white" data-testid={`consign-bar-${room}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b-2 border-[#b42318] bg-[#fef3f2] px-2.5 py-1.5">
                <span className="border border-[#b42318] bg-[#b42318] px-1.5 py-px text-[11px] font-bold uppercase tracking-[0.06em] text-white bo-inv"
                    data-testid={`consign-bar-state-${room}`}>
                    <Lock size={10} className="mr-1 inline" /> locked · on consignment
                </span>
                <p className="flex items-center gap-1.5 text-[13px] font-semibold text-[#1a1a1a]">
                    <Truck size={13} className="shrink-0 text-[#b42318]" />
                    13th Ave prints, packs and ships every {info.label.toLowerCase()} order. You do nothing but sell.
                </p>
                <Link to="/account/consignment" data-testid={`consign-bar-settings-${room}`}
                    className="ml-auto text-[12px] font-semibold text-[#1a56db] hover:underline">
                    All consignment settings →
                </Link>
            </div>

            <p className="border-b border-[#d0d0d0] px-2.5 py-1.5 text-[13px] text-[#1a1a1a]"
                data-testid={`consign-bar-note-${room}`}>
                These are 13th Ave's goods sitting on your shelf, so nothing here can be renamed, repriced,
                photographed, hidden or deleted. <span className="font-semibold">Your percent over is the one thing
                    you set</span> — and every cent of it is yours.
            </p>

            <div className="flex flex-wrap items-end gap-x-4 gap-y-2 p-2.5">
                <label className="flex items-end gap-1.5">
                    <span className="pb-1 text-[11px] font-semibold uppercase text-[#4a4a4a]">Percent over</span>
                    <input type="number" min="0" max={maxMarkup} step="0.5" value={pct}
                        onChange={(e) => setPct(e.target.value)} data-testid={`consign-markup-${room}`}
                        className={`${cellCls} w-16`} />
                    <span className="pb-1 text-[13px] font-semibold text-[#1a1a1a]">%</span>
                </label>
                <Btn tone="solid" disabled={busy || String(info.markup_pct) === String(Number(pct) || 0)}
                    testid={`consign-markup-save-${room}`} onClick={saveMarkup}>Save this shelf</Btn>
                {info.override ? (
                    <button onClick={useDefault} disabled={busy} data-testid={`consign-markup-default-${room}`}
                        className="pb-1 text-[12px] font-semibold text-[#1a56db] hover:underline">
                        use my shop default instead
                    </button>
                ) : (
                    <span className="pb-1 text-[12px] text-[#4a4a4a]" data-testid={`consign-markup-inherited-${room}`}>
                        following your shop default · 0–{maxMarkup}% allowed
                    </span>
                )}

                <p className="pb-0.5 text-[13px] text-[#1a1a1a]" data-testid={`consign-example-${room}`}>
                    <span className="font-semibold">Live example:</span> a {money(example)} job sells for{" "}
                    <span className="font-bold tabular-nums">{money(live)}</span> in your shop — you keep{" "}
                    <span className="font-bold tabular-nums">
                        {money(example * rate + (live - example))}
                    </span> of it.
                </p>

                <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1.5">
                    <span className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold uppercase text-[#4a4a4a]">Who prints it</span>
                        <Toggle on={false} disabled={busy} testid={`consign-take-over-${room}`}
                            labelOn="Me" labelOff="13th Ave" onChange={() => setPrivate(true)} />
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold uppercase text-[#4a4a4a]">Counter</span>
                        <Toggle on={info.counter_open} disabled={busy} testid={`consign-counter-${room}`}
                            labelOn="Open" labelOff="Closed" onChange={setCounter} />
                    </span>
                    <Btn testid={`consign-money-toggle-${room}`} onClick={() => setOpen((v) => !v)}>
                        Where the money goes <ChevronDown size={11} className={open ? "rotate-180" : ""} />
                    </Btn>
                </div>
            </div>

            {open && <MoneyMap example={example} pct={Number(pct) || 0} rate={rate} />}
        </div>
    );
}
